import { CommonModule } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  effect,
  inject,
} from '@angular/core'
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco'
import { DefaultButtonFilledComponent } from 'src/app/_components/buttons/default-button-filled/default-button-filled.component'
import { DefaultButtonOutlinedComponent } from 'src/app/_components/buttons/default-button-outlined/default-button-outlined.component'
import {
  ToastMessage,
  ToastType,
} from 'src/app/_components/toast/toast.component'
import { WrapperModalComponent } from 'src/app/_components/modals/wrapper-modal/wrapper-modal.component'
import { NetworkDiagnosticsService } from 'src/app/_services/network-diagnostics.service'
import { ToastService } from 'src/app/_services/toast.service'

@Component({
  selector: 'network-block-modal',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    WrapperModalComponent,
    DefaultButtonFilledComponent,
    DefaultButtonOutlinedComponent,
  ],
  providers: [provideTranslocoScope('modalNetworkBlock')],
  templateUrl: './network-block-modal.component.html',
})
export class NetworkBlockModalComponent {
  @ViewChild('modalContainer') modalContainer?: ElementRef<HTMLElement>

  readonly diagnostics = inject(NetworkDiagnosticsService)
  private readonly toastService = inject(ToastService)

  detailsExpanded = false

  constructor() {
    effect(() => {
      if (this.diagnostics.modalVisible()) {
        queueMicrotask(() => this.setFocusOnModal())
      }
    })
  }

  get isVisible(): boolean {
    return this.diagnostics.modalVisible()
  }

  get isBlocked(): boolean {
    return this.diagnostics.displayState() === 'blocked'
  }

  ngAfterViewInit(): void {
    this.setFocusOnModal()
  }

  @HostListener('document:keydown.escape')
  handleEscape(): void {
    if (!this.isVisible) {
      return
    }
    this.handleClose()
  }

  setFocusOnModal(): void {
    this.modalContainer?.nativeElement.focus()
  }

  handleClose(): void {
    this.diagnostics.dismiss()
  }

  async handleTryAgain(): Promise<void> {
    await this.diagnostics.retry()
  }

  async handleCopyDetails(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.diagnostics.getDetailsText())
      this.toastService.triggerToast(
        ToastType.Success,
        ToastMessage.Details_Copied
      )
    } catch {
      // Clipboard may be unavailable; details remain visible in the modal.
    }
  }

  toggleDetails(): void {
    this.detailsExpanded = !this.detailsExpanded
  }

  getTitle(t: (key: string, params?: Record<string, string>) => string): string {
    return this.isBlocked
      ? t('blockedTitle')
      : t('offlineTitle')
  }

  formatLastChecked(date: Date): string {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  getRetryFeedbackMessage(
    t: (key: string, params?: Record<string, string>) => string
  ): string {
    const feedback = this.diagnostics.retryFeedback()
    if (feedback === 'still-blocked') {
      return t('tryAgainStillBlocked')
    }
    return t('tryAgainStillOffline')
  }
}
